import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
  TrendingUp, 
  Calendar, 
  BarChart3, 
  Activity,
  CheckCircle,
  ArrowRight,
  Star,
  Users,
  Zap
} from 'lucide-react';
import useAuthStore from '../stores/authStore';

const LandingPage = () => {
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();

  // Check for email confirmation tokens in URL hash and redirect
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('access_token=') && hash.includes('type=signup')) {
      // Redirect to confirmation page with the hash
      navigate('/auth/confirm' + hash);
    }
  }, [navigate]);

  const features = [
    {
      icon: TrendingUp,
      title: "Gain competitor pricing insights",
      description: "Real-time competitive analysis to optimize your pricing strategy"
    },
    {
      icon: Calendar,
      title: "Identify local events to set rates",
      description: "Leverage local event data to maximize revenue opportunities"
    },
    {
      icon: BarChart3,
      title: "Analyze rate evolution trends",
      description: "Historical and predictive analytics for informed decision making"
    },
    {
      icon: Activity,
      title: "Deep dive realtime demand forecast",
      description: "AI-powered demand predictions with 90-day forecasting accuracy"
    }
  ];

  const stats = [
    { label: "Hotels Served", value: "500+", icon: Users },
    { label: "Accuracy Rate", value: "94%", icon: Star },
    { label: "Revenue Increase", value: "23%", icon: TrendingUp },
    { label: "Real-time Updates", value: "24/7", icon: Zap }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Hero Title */}
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              AI Powered{' '}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Market Insight
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
              Transform your hotel revenue management with intelligent demand forecasting, 
              competitive analysis, and real-time market insights powered by advanced AI.
            </p>

            {/* Feature List */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 max-w-4xl mx-auto">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="flex items-start space-x-4 text-left">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg">
                        <Icon className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {isAuthenticated ? (
                <Button asChild size="lg" className="text-lg px-8 py-3">
                  <Link to="/app" className="flex items-center space-x-2">
                    <span>Go to Dashboard</span>
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </Button>
              ) : (
                <>
                  <Button asChild size="lg" className="text-lg px-8 py-3">
                    <Link to="/register" className="flex items-center space-x-2">
                      <span>Start Free Trial</span>
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                  </Button>
                  <Button variant="outline" size="lg" className="text-lg px-8 py-3">
                    <a href="#features">Learn More</a>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center">
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mx-auto mb-4">
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-gray-600">
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Powerful Features for Revenue Optimization
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to maximize your hotel's revenue potential with data-driven insights
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "90-Day Demand Forecast",
                description: "AI-powered predictions with threshold analysis and seasonal pattern recognition",
                icon: Activity
              },
              {
                title: "Competitive Rate Analysis",
                description: "Real-time competitor pricing matrix with market positioning insights",
                icon: BarChart3
              },
              {
                title: "Room Type Performance",
                description: "Detailed analytics for each room category with optimization recommendations",
                icon: TrendingUp
              },
              {
                title: "Event Impact Analysis",
                description: "Local event correlation with demand spikes and pricing opportunities",
                icon: Calendar
              },
              {
                title: "Multi-Tenant Management",
                description: "Centralized platform for managing multiple hotel properties and brands",
                icon: Users
              },
              {
                title: "Real-Time API Access",
                description: "Integrate demand data directly into your existing revenue management systems",
                icon: Zap
              }
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mb-4">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600">
              Choose the plan that fits your hotel's needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                name: "Starter",
                price: "$99",
                period: "/month",
                description: "Perfect for single properties",
                features: [
                  "90-day demand forecasting",
                  "Basic competitor analysis",
                  "Email support",
                  "API access"
                ]
              },
              {
                name: "Professional",
                price: "$299",
                period: "/month",
                description: "Ideal for hotel groups",
                features: [
                  "Everything in Starter",
                  "Multi-property management",
                  "Advanced analytics",
                  "Priority support",
                  "Custom integrations"
                ],
                popular: true
              },
              {
                name: "Enterprise",
                price: "Custom",
                period: "",
                description: "For large hotel chains",
                features: [
                  "Everything in Professional",
                  "Unlimited properties",
                  "Dedicated account manager",
                  "Custom development",
                  "SLA guarantee"
                ]
              }
            ].map((plan, index) => (
              <Card key={index} className={`relative ${plan.popular ? 'border-blue-500 shadow-lg' : ''}`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-gray-600">{plan.period}</span>
                  </div>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className="w-full mt-6" 
                    variant={plan.popular ? "default" : "outline"}
                    asChild
                  >
                    <Link to={plan.name === "Enterprise" ? "/login" : "/register"}>
                      {plan.name === "Enterprise" ? "Contact Sales" : "Start Free Trial"}
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Optimize Your Revenue?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join hundreds of hotels already using AI-powered insights to maximize their revenue potential.
          </p>
          <Button size="lg" variant="secondary" className="text-lg px-8 py-3" asChild>
            <Link to="/login" className="flex items-center space-x-2">
              <span>Start Your Free Trial Today</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
